/* DataExplorerDetailsViewComponent.js */

"use strict";
const _ = require("lodash");
const EventEmitter = require("events").EventEmitter;
const Renderer = require("./DataExplorerDetailsViewRenderer");
const CreateComponent = require("./veils/saveAs/CreateComponent");

class DataExplorerDetailsViewComponent extends EventEmitter {
    constructor(logger, gameEditor, userSession, MUF, ExplorerTemplate, Explorables) {
        super();
        this._logger = logger;
        this._gameEditor = gameEditor;
        this._userSession = userSession;
        this._modalUserFeedback = MUF;
        this._ExplorerTemplate = ExplorerTemplate;
        this._templateEntity = undefined;
        this._Explorables = Explorables;
        this._explorablesEntity = undefined;
        this._osImage = undefined;
        this._activeOrgId = userSession.activeOrganization.id;
        this._hasModeratorAuth = userSession.authorizedActions.indexOf("explorerTemplateModerate") > 0;
        this._rowOrientationKey = undefined;
        this._updatedRowOrientationKey = undefined;
        this._isOwnUser = false; // change to false to enable logic
        this._isOwnOrg = false;
        this._rowOrientationGroups = undefined;
        this._renderer = new Renderer(this);
        this._registerListeners();
    }

    get templateEntity() {
        return this._templateEntity;
    }

    get isDirty() {
        return this._templateEntity ? this._templateEntity.isDirty() : false;
    }

    /*******************************************/
    /* START PRIVATE COMPONENT MODEL METHODS   */
    /*******************************************/

    _registerListeners() {}

    _unregisterListeners() {}

    _registerTemplateListeners() {
        this._templateEntity.on("DirtyStateChanged", (this._onDirtyStateChanged = this._onDirtyStateChanged.bind(this)));
        this._templateEntity.on("PristinePropertiesChanged", (this._onPristinePropertiesChanged = this._onPristinePropertiesChanged.bind(this)));
    }

    _unRegisterTemplateListeners() {
        this._templateEntity.removeListener("DirtyStateChanged", this._onDirtyStateChanged);
        this._templateEntity.removeListener("PristinePropertiesChanged", this._onPristinePropertiesChanged);
    }

    /*******************************************/
    /* END PRIVATE COMPONENT MODEL METHODS     */
    /* START PUBLIC COMPONENT MODEL METHODS    */
    /*******************************************/

    get renderer() {
        return this.getRenderer();
    }
    getRenderer() {
        return this._renderer;
    }

    get gameEditor() {
        return this.getGameEditor();
    }
    getGameEditor() {
        return this._gameEditor;
    }

    shutdown() {
        if (!_.isUndefined(this._templateEntity)) {
            this._unRegisterTemplateListeners();
        }
        this._unregisterListeners();
        return this._renderer.shutdown().then(
            () => {
                this._osImage = undefined;
                this._logger = undefined;
                this._gameEditor = undefined;
                this._renderer = undefined;
            },
            (err) => {
                this._logger.warn(err, "DataExplorerDetailsViewComponent.shutdown() error.");
            }
        );
    }

    get logger() {
        return this.getLogger();
    }
    getLogger() {
        return this._logger;
    }

    /*******************************************/
    /* END COMPONENT MODEL METHODS             */
    /* START HANDLER METHODS                   */
    /*******************************************/

    _onDirtyStateChanged(dirtyState) {
        this.emit("Dirty", dirtyState);
        this._gameEditor._tabCmpnt.setDirty(dirtyState);
        let onBeforeUnloadFunc = () => {
            return this._modalUserFeedback
                .displayConfirmation("Would you like to Save or Discard your changes?", {
                    title: "Unsaved Changes",
                    confirmDetails: {
                        text: "Save",
                        type: "additive"
                    },
                    rejectDetails: {
                        text: "Discard",
                        type: "text"
                    }
                })
                .then(() => {
                    // return this.saveTemplate(false).then(() => undefined); // it will not close the tab if it will have something in result
                    // console.log(this._templateEntity);
                    return this._saveTemplate()
                        .then(() => this._gameEditor.getTabCmpnt().setDirty(false))
                        .then(() => undefined);
                })
                .catch((err) => {
                    if (err.userRejected) {
                        return Promise.resolve(this._templateEntity.clearPendingPatches(true))
                            .then(() => this._modalUserFeedback.hideDisplayed())
                            .then(() => this._gameEditor.getTabCmpnt().setDirty(false))
                            .then(() => undefined);
                    } else {
                        console.error("Error in onBeforeUnloadFunc! Err:", err);
                    }
                });
        };

        if (this.isDirty) {
            this._gameEditor.setOnBeforeUnload(onBeforeUnloadFunc);
        } else {
            this._gameEditor.clearOnBeforeUnload();
        }
    }

    _onPendingPatchesChanged() {
        // TODO - this is a quick hack because the Array ref adapter does a shutdown and complete rebuild every time you call setValue. the effect to the user is they lose focus in the proxy list if they stop typing longer than the debounce
        // this.emit('FilePropertiesChanged');
    }

    _onPristinePropertiesChanged(pristineProperties, before) {
        // console.log(this._templateEntity.updatedOn);
        // this._lastUpdated = this._templateEntity.updatedOn;
        this.emit("TemplatePropertiesChanged", pristineProperties, before);
    }

    /*******************************************/
    /* END HANDLER METHODS                     */
    /* START PRIVATE METHODS                   */
    /*******************************************/

    _generteTreeData(explorables) {
        const restructured = [];
        for (let key in explorables) {
            restructured.push(this._generteTreeNode(key, explorables[key]));
        }
        return restructured;
    }

    _generteTreeNode(name, nodeData) {
        if (!nodeData.path) {
            const node = {};
            node.text = name;
            node.children = [];
            for (let key in nodeData) {
                node.children.push(this._generteTreeNode(key, nodeData[key]));
            }
            return node;
        }
        return name;
    }

    _generateColumns(fieldDefs = [], silent = false) {
        let columns = [];
        let hasSort = false;
        for (let i = 0; i < fieldDefs.length; i++) {
            const paths = fieldDefs[i].field.split(">");
            const field = paths.reduce((acc, path) => acc[path], fieldDefs[i].solutions);
            let column = Object.assign({}, field);
            column.flattenedPath = fieldDefs[i].field;
            column.visible = fieldDefs[i].visible;
            column.path = column.path || fieldDefs[i].path;
            column.dataType = column.dataType || fieldDefs[i].dataType;
            column.typeInfo = column.typeInfo || fieldDefs[i].typeInfo;
            if (fieldDefs[i].heading) {
                // if(fieldDefs[i].rowOrientation) {
                //     column.rowOrientedValHeading = fieldDefs[i].heading;
                // } else {
                column.heading = fieldDefs[i].heading;
                // }
            } else {
                column.heading = ""; // setting to string so that it doesnt go undefined
            }
            if (fieldDefs[i].sortType) {
                column.sort = {
                    order: fieldDefs[i].sortOrder,
                    descending: fieldDefs[i].sortType === "desc" ? true : false
                };
                hasSort = true;
            } else {
                column.sort = undefined;
            }
            if (fieldDefs[i].conditionalFormat) {
                column.formatting = fieldDefs[i].conditionalFormat;
            }

            column.aggregator = fieldDefs[i].aggregator;

            columns.push(column);
        }
        if (!silent) {
            this.emit("SortAvailableChange", hasSort);
        }
        return columns;
    }

    _fixSortOrder(fields = []) {
        const sortedFields = fields
            .filter((field) => field.sortType)
            .sort((f1, f2) => f1.sortOrder - f2.sortOrder)
            .map((f, idx) => {
                f.sortOrder = idx; // mutates the original field objects
                return f;
            });
        return sortedFields || [];
    }

    _saveTemplate() {
        if (this._templateEntity.updatedOn > this._lastUpdated) {
            return this._modalUserFeedback
                .displayConfirmation(
                    "Template has been saved from a different tab/window. Saving will overwrite the changes.\nWould you like to proceed?",
                    {
                        title: "Newer data available",
                        confirmDetails: {
                            text: "Save",
                            type: "additive"
                        },
                        rejectDetails: {
                            text: "Cancel",
                            type: "text"
                        }
                    }
                )
                .then(() => {
                    // return this.saveTemplate(false).then(() => undefined); // it will not close the tab if it will have something in result
                    return this._templateEntity.update().then((entity) => {
                        // console.log(this._lastUpdated, entity.updatedOn, entity );
                        this._lastUpdated = entity.updatedOn;
                        return Promise.resolve();
                    });
                })
                .catch((err) => {
                    this._logger.warn("Template not saved :: Newer data available", err);
                    return Promise.reject(err);
                });
        } else {
            return this._templateEntity.update().then((entity) => {
                // console.log(this._lastUpdated, entity.updatedOn, entity );
                this._lastUpdated = entity.updatedOn;
                return Promise.resolve();
            });
        }
    }

    _clearChanges() {
        return Promise.resolve().then(() => this._templateEntity.clearPendingPatches());
        // .then(() => (this._updatedRowOrientationKey = undefined));
    }
    /*******************************************/
    /* END PRIVATE METHODS                     */
    /* START PUBLIC API METHODS                */
    /*******************************************/

    whenModelIsReady() {
        return this._gameEditor
            .whenEditorIsReady()
            .then(() => {
                this._templateEntity = this._gameEditor.template;
                this._rowOrientationKey = this._templateEntity.rowOrientedProp;
                this._lastUpdated = this._templateEntity.updatedOn;
                // console.log(this._userSession, template);
                if (this._templateEntity.tenantId === _.get(this._userSession, "activeOrganization.id", "")) {
                    this._isOwnOrg = true;
                }
                const userId = _.get(this._userSession, "user.id", undefined); //change to ownerId when available
                if (
                    userId &&
                    this._templateEntity.ownerId &&
                    userId === this._templateEntity.ownerId &&
                    this._templateEntity.tenantId === _.get(this._userSession, "activeOrganization.id", "")
                ) {
                    this._isOwnUser = true;
                }

                if (_.get(this._userSession, "authorizedActions", []).indexOf("explorerTemplateWrite") < 0) {
                    this._isOwnUser = false; // setting to false, handles case where user previously had access
                }
                return this._Explorables.loadExplorables(this._activeOrgId);
            })
            .then((explorablesEnity) => {
                this._explorablesEntity = explorablesEnity;
                this._rowOrientationGroups = Object.assign({}, explorablesEnity.getRowOrientedProps());
                this._categories = Object.keys(_.get(explorablesEnity, "_pristineProperties.explorables", {}));
                // this._solnDataForCat = this._generateSolutionsData(_.get(explorablesEnity, '_pristineProperties.explorables', {}));
                this._dataForTree = this._generteTreeData(explorablesEnity.getExplorables());
                this._registerTemplateListeners();
                return Promise.resolve();
            });
    }

    resetFileProperties() {
        this._osImage.clearPendingPatches();
        return Promise.resolve();
    }

    findFlattenedPath(obj, val) {
        let flattenedKey = "";
        for (let key in obj) {
            flattenedKey = "";
            if (typeof obj[key] === "object" && !obj[key].path) {
                const subKey = this.findFlattenedPath(obj[key], val);
                if (subKey) {
                    flattenedKey += key + ">" + subKey;
                    break;
                }
            }

            if (obj[key].path && _.isEqual(obj[key].path, val)) {
                flattenedKey += key;
                break;
            }
        }
        return flattenedKey;
    }

    _requestTemplateCreate(isNotUniq, formVals, slickVals) {
        this._modalUserFeedback
            .displayCustom(
                new CreateComponent(
                    this._logger,
                    "Save Template",
                    isNotUniq
                        ? "The entered name is not unique. Please specify a unique name to save template."
                        : "Please specify a unique name to save the template.",
                    "Enter Template Name",
                    formVals.name
                ),
                { dontHide: true }
            )
            .then((result) => {
                return this._modalUserFeedback.displayLoading().then(() => {
                    return this._cloneTemplate(result.trim(), formVals, slickVals);
                });
            })
            .catch((err) => {
                if (err && err.userRejected) {
                    this._logger.warn({ error: err }, "SaveAs - User Canceled");
                    return this._modalUserFeedback.hideDisplayed();
                } else {
                    return this._cloneErrorHandler(err, formVals, slickVals);
                }
            });
    }

    _cloneErrorHandler(error, formVals, slickVals) {
        const { userRejected, errorCode } = error;
        if (userRejected) {
            return;
        }
        this._logger.warn({ error }, "SaveAs error.");
        switch (errorCode) {
            case "EntityAlreadyExists":
                return this._requestTemplateCreate(true, formVals, slickVals);
            default:
                return this._showCloneError(error);
        }
    }

    _showCloneError(error) {
        return this._modalUserFeedback.displayError({ error }).catch((error) => {
            if (error.userRejected) {
                return;
            }
            this._logger.warn({ error }, "SaveAs action error.");
        });
    }

    _cloneTemplate(templateName, formVals, slickVals) {
        return this._templateEntity
            .clone(templateName)
            .then((clone) => {
                clone.setProperty("lastWorkId", "");
                for (let key in formVals) {
                    if (key !== "name") {
                        clone[key] = formVals[key];
                    }
                }
                // clone.ownerId = this._userSession.user.id;
                // clone.ownerName = this._userSession.user.name;
                clone.setColumns(this._generateColumns(slickVals, true));
                // clone.setRowOrientedProp(this._updatedRowOrientationKey);
                return clone.update();
            })
            .then((entity) => {
                this.emit("RefreshView");
                return this._modalUserFeedback.hideDisplayed().then(() => {
                    _.delay(() => this._gameEditor._LMRBLayout.emit("SelectRowsOnTemplateGrid", [entity.id]), 200);
                    return new Promise((resolve) => {
                        _.delay(() => {
                            resolve(this._gameEditor.launchNewTab(entity));
                        }, 200);
                    });
                });
            });
    }

    /*******************************************/
    /* END PUBLIC API METHODS                  */
    /*******************************************/
}

module.exports = DataExplorerDetailsViewComponent;

DataExplorerDetailsViewComponent.$inject = [
    "$teve.logger",
    "gameEditor.gameEditor",
    "$teve.userSession",
    "$teve.ui.userFeedback",
    "$teve.entities.ExplorerTemplate",
    "$teve.entities.Explorables"
];

DataExplorerDetailsViewComponent.$displayDetails = {
    type: "View",
    name: "Data Explorer Details",
    icon: "dataexplorer.edit",
    shown: true,
    title: "Data Explorer Details",
    order: 1
};
